import React, { useCallback, useState } from 'react';
import { Button, Flex, Heading, Text, TextField } from '@vibe/core';
import { reopenTimesheet } from '../../api/timesheet-approval.api';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';

export interface TimesheetReopenDialogProps {
  timesheetId: string;
  onSuccess: (updated: TimesheetSubmission) => void;
  onClose: () => void;
}

/**
 * Dialog to reopen an approved timesheet with a mandatory reason.
 */
const TimesheetReopenDialog: React.FC<TimesheetReopenDialogProps> = ({
  timesheetId,
  onSuccess,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = reason.trim().length >= 1;

  const handleReopen = useCallback(async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);

    try {
      const result = await reopenTimesheet(timesheetId, { reason });
      onSuccess(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }, [timesheetId, reason, isValid, onSuccess]);

  return (
    <div className="rp-ts-dialog-overlay" onClick={onClose}>
      <div
        className="rp-ts-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <Heading type={Heading.types.H3}>Reopen Timesheet</Heading>

        <div className="rp-ts-dialog__body">
          <Text type={Text.types.TEXT2}>
            Reopening this timesheet will unlock worklogs for the period.
            The submitter will need to resubmit for approval.
          </Text>

          <TextField
            title="Reason for reopening (required)"
            placeholder="Explain why the timesheet is being reopened..."
            value={reason}
            onChange={(val: string) => setReason(val)}
            size={TextField.sizes.MEDIUM}
          />

          {error && (
            <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
              {error}
            </Text>
          )}
        </div>

        <Flex
          gap={Flex.gaps.SMALL}
          justify={Flex.justify.END}
          className="rp-ts-dialog__actions"
        >
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            onClick={handleReopen}
            loading={submitting}
            disabled={!isValid}
          >
            Reopen
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default TimesheetReopenDialog;
